import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, X, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateOffer = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({
    description: '',
    biome: '',
    total_area: '',
    forest_area: '',
    price: '',
    carbon_credits: ''
  });

  React.useEffect(() => {
    if (!user) {
      toast.error('Por favor, faça login para criar uma oferta');
      navigate('/auth');
    }
  }, [user, navigate]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (!file.name.endsWith('.pdf')) {
        toast.error('Apenas arquivos PDF são permitidos');
        continue;
      }

      try {
        // Check if S3 is configured by trying to request upload URL
        try {
          const urlResponse = await axios.post(`${API}/request-upload-url`,
            new URLSearchParams({
              file_name: file.name,
              file_type: file.type
            }),
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Bearer ${token}`
              }
            }
          );

          const { upload_url, file_id, s3_key } = urlResponse.data;

          // Upload directly to S3
          await axios.put(upload_url, file, {
            headers: {
              'Content-Type': file.type
            }
          });

          // Confirm upload
          await axios.post(`${API}/confirm-upload`,
            new URLSearchParams({
              file_id,
              s3_key,
              file_name: file.name
            }),
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                Authorization: `Bearer ${token}`
              }
            }
          );

          setUploadedFiles(prev => [...prev, {
            id: file_id,
            name: file.name,
            s3_key
          }]);
          toast.success(`${file.name} enviado para o S3 com sucesso`);
        } catch (s3Error) {
          // If S3 is not configured, fall back to traditional upload
          if (s3Error.response?.status === 503) {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(`${API}/upload`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${token}`
              }
            });

            setUploadedFiles(prev => [...prev, {
              id: response.data.file_id,
              name: response.data.filename
            }]);
            toast.success(`${file.name} enviado com sucesso (local storage)`);
          } else {
            throw s3Error;
          }
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        console.error('Upload error:', error);
      }
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (uploadedFiles.length === 0) {
      toast.error('Por favor, faça upload de pelo menos um documento PDF');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      submitData.append('documents', uploadedFiles.map(f => f.id).join(','));

      const response = await axios.post(`${API}/offers`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      });

      toast.success('Oferta criada com sucesso!');
      navigate(`/offers/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha ao criar oferta');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-[calc(100vh-4rem)] py-12 px-6 md:px-12 lg:px-24">
      <div className="container mx-auto max-w-3xl">
        <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
          Vender <span className="text-primary">Créditos de Carbono</span>
        </h1>
        <p className="text-zinc-400 text-lg mb-12">
          Liste seus créditos de carbono verificados no marketplace
        </p>

        <Card className="bg-card border-border rounded-sm" data-testid="create-offer-form">
          <CardHeader>
            <CardTitle className="text-2xl font-heading font-bold">Detalhes da Oferta</CardTitle>
            <CardDescription className="text-zinc-400">
              Forneça informações precisas sobre seus créditos de carbono
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="documents">Upload de Documentos (PDF) *</Label>
                <div className="border-2 border-dashed border-border rounded-sm p-8 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    id="documents"
                    accept=".pdf"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="documents" className="cursor-pointer">
                    <Upload className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                    <p className="text-zinc-400 mb-2">Clique para fazer upload dos arquivos PDF</p>
                    <p className="text-sm text-zinc-600">Reservas legais, certificações, documentos de propriedade</p>
                  </label>
                </div>
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2 mt-4">
                    {uploadedFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between bg-secondary p-3 rounded-sm" data-testid={`uploaded-file-${file.id}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-primary" />
                          <span className="text-sm">{file.name}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(file.id)}
                          className="text-zinc-400 hover:text-red-500 transition-colors"
                          data-testid={`remove-file-${file.id}`}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição da propriedade *</Label>
                <Textarea
                  id="description"
                  placeholder="Descreva sua propriedade e a geração de créditos de carbono..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm"
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="biome">Bioma *</Label>
                <Select
                  value={formData.biome}
                  onValueChange={(value) => setFormData({ ...formData, biome: value })}
                  required
                >
                  <SelectTrigger className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm h-12" data-testid="biome-select">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="Amazon Rainforest">Floresta Amazônica</SelectItem>
                    <SelectItem value="Atlantic Forest">Floresta Atlântica</SelectItem>
                    <SelectItem value="Cerrado">Cerrado</SelectItem>
                    <SelectItem value="Caatinga">Caatinga</SelectItem>
                    <SelectItem value="Pantanal">Pantanal</SelectItem>
                    <SelectItem value="Pampa">Pampa</SelectItem>
                    <SelectItem value="Other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="total_area">Area Total (hectares) *</Label>
                  <Input
                    id="total_area"
                    type="number"
                    step="0.01"
                    placeholder="100.50"
                    value={formData.total_area}
                    onChange={(e) => setFormData({ ...formData, total_area: e.target.value })}
                    required
                    className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm h-12 font-mono"
                    data-testid="total-area-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="forest_area">Área de Floresta/Reserva (hectares) *</Label>
                  <Input
                    id="forest_area"
                    type="number"
                    step="0.01"
                    placeholder="75.25"
                    value={formData.forest_area}
                    onChange={(e) => setFormData({ ...formData, forest_area: e.target.value })}
                    required
                    className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm h-12 font-mono"
                    data-testid="forest-area-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="carbon_credits">Créditos de Carbono (tCO₂) *</Label>
                  <Input
                    id="carbon_credits"
                    type="number"
                    step="0.01"
                    placeholder="500"
                    value={formData.carbon_credits}
                    onChange={(e) => setFormData({ ...formData, carbon_credits: e.target.value })}
                    required
                    className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm h-12 font-mono"
                    data-testid="carbon-credits-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Preço (R$) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    placeholder="25000"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    className="bg-secondary border-border text-white focus:border-primary focus:ring-1 focus:ring-primary rounded-sm h-12 font-mono"
                    data-testid="price-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-black hover:bg-[#B3E600] hover:shadow-[0_0_20px_rgba(204,255,0,0.3)] rounded-sm font-medium tracking-wide transition-all duration-200 h-12"
                data-testid="submit-offer-button"
              >
                {loading ? 'Criando Oferta...' : 'Criar Oferta'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateOffer;
